import React from 'react';
import { useHistory } from 'react-router-dom';
import UserRatingDisplay from '../../../components/common/UserRatingDisplay';
import UserProfilePic from '../../../components/common/UserProfilePic';
import { useFollowUserMutation, useGetMerchantFullProfileQuery, useGetProfileQuery } from '../../../redux/apis/endpoints/profileApi';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';

const MerchantInfo = ({
  merchant,
  onMessageMerchant,
  className = ''
}) => {
  const history = useHistory();

  if (!merchant) return null;

  const displayName = (merchant.firstname || merchant.lastname)
    ? `${merchant.firstname || ''} ${merchant.lastname || ''}`.trim()
    : merchant.username;

  let merchantLocation = null;
  if (merchant.defaultAddress) {
    merchantLocation = merchant.defaultAddress;
  } else if (merchant.addresses && merchant.addresses.length > 0) {
    merchantLocation = merchant.addresses.find(addr => addr.isDefault) || merchant.addresses[0];
  }

  const handleProfileClick = () => {
    history.push(`/merchant/${merchant.id}`);
  };

  return (
    <div className={`border-t border-gray-200 pt-6 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={handleProfileClick}
          >
            <UserProfilePic
              user={merchant}
              className="h-12 w-12"
              altText={displayName}
            />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <h3
              className="text-sm font-medium text-gray-900 cursor-pointer hover:text-brand-teal truncate"
              onClick={handleProfileClick}
            >
              {displayName}
            </h3>
            <MerchantInfoSubline merchant={merchant} onProfileClick={handleProfileClick} />
          </div>
        </div>
        <div className="flex-shrink-0">
          <FollowButton merchantId={merchant.id} />
        </div>
      </div>
    </div>
  );
};

/**
 * Shows rating, bio (truncated with ...more), or school name as fallback
 */
const MerchantInfoSubline = ({ merchant, onProfileClick }) => {
  const { data: userReviewsData } = require('../../../redux/apis/endpoints/reviewApi').useGetUserReviewsReceivedQuery(
    { userId: merchant?.id, pageSize: 500 },
    { skip: !merchant?.id }
  );

  const reviews = userReviewsData || [];
  const hasReviews = reviews.length > 0;

  if (hasReviews) {
    return <UserRatingDisplay userId={merchant?.id} ratingComponentSize="sm" className="mt-1" />;
  }

  // No reviews — show bio truncated to 1 line with "...more"
  if (merchant.bio) {
    return (
      <p className="mt-1 text-xs text-gray-500 truncate">
        {merchant.bio}{' '}
        <span
          className="text-brand-teal cursor-pointer hover:underline inline"
          onClick={onProfileClick}
        >
          ...more
        </span>
      </p>
    );
  }

  // No bio and no reviews but has school — show school
  if (merchant.school) {
    return (
      <p className="mt-1 text-xs text-gray-400">
        {merchant.school}
      </p>
    );
  }

  return null;
};

const FollowButton = ({ merchantId }) => {
  const history = useHistory();
  const { isAuthenticated } = useSelector(state => state.auth);
  const { data: currentUser } = useGetProfileQuery(undefined, { skip: !isAuthenticated });
  const { data: merchantData, isLoading: isMerchantLoading } = useGetMerchantFullProfileQuery(merchantId);
  const [followUser, { isLoading: isFollowing }] = useFollowUserMutation();

  const isSelf = currentUser?.id === merchantId;
  const isFollowingUser = merchantData?.isFollowing;

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      history.push('/login');
      return;
    }

    try {
      await followUser(merchantId).unwrap();
      toast.success(isFollowingUser ? 'Unfollowed designer' : 'Following designer');
    } catch (err) {
      toast.error('Failed to update follow status');
    }
  };

  if (isSelf) return null;

  return (
    <button
      onClick={handleFollow}
      disabled={isFollowing || isMerchantLoading}
      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal transition-colors ${isFollowingUser
        ? 'border-brand-teal text-brand-teal bg-white hover:bg-brand-teal/5'
        : 'border-transparent text-white bg-brand-teal hover:bg-brand-teal-dark'
        }`}
    >
      {isFollowing ? 'Processing...' : isFollowingUser ? 'Unfollow' : 'Follow'}
    </button>
  );
};

export default MerchantInfo;